import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, FileText, AlertTriangle } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { FileAzioni } from "./FileViewer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";

const CATEGORIE = ["Documento identità", "Tessera sanitaria", "Codice fiscale", "Cartella clinica", "Certificato autonomia", "Piano terapeutico", "Prescrizioni", "Allergie", "Esenzione", "Contratto", "Documentazione ADI", "Consenso privacy", "Contratto di lavoro", "Curriculum", "Attestato", "Idoneità sanitaria", "Altro"];

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-");

export default function Documenti({ ownerType, ownerId, ownerNome, structureId, embedded = true, categories, docFilter, defaultCategoria, testidSuffix, obbligatori = [], smart }) {
  const CATS = categories || CATEGORIE;
  const [q, setQ] = useState("");
  const [fCat, setFCat] = useState("tutti");
  const catOf = (d) => (obbligatori.find(([, alias]) => (alias || []).includes(d.categoria))?.[0]) || d.categoria || "Altro";
  const [docs, setDocs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const qc = useQueryClient();
  const load = async () => {
    const { data } = await api.get("/documenti");
    setDocs(data.filter((d) => d.owner_type === ownerType && (!ownerId || d.owner_id === ownerId)));
    setLoaded(true);
    if (ownerType === "operatore") qc.invalidateQueries({ queryKey: ["op-conformita"] });
  };
  useEffect(() => { load(); }, [ownerType, ownerId]);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Seleziona un file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("owner_type", ownerType);
      if (ownerId) fd.append("owner_id", ownerId);
      if (ownerNome) fd.append("owner_nome", ownerNome);
      if (structureId) fd.append("structure_id", structureId);
      Object.entries(meta).forEach(([k, v]) => v && fd.append(k, v));
      await api.post("/documenti/upload", fd);
      toast.success("Documento caricato");
      setOpen(false); setMeta({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore caricamento"); }
    finally { setUploading(false); }
  };

  const fa = (d, size) => <FileAzioni url={`/documenti/${d.id}/download`} titolo={d.original_filename || d.titolo || "documento"} testid={d.id} size={size} />;
  const remove = async (id) => { await api.delete(`/documenti/${id}`); toast.success("Eliminato"); load(); };

  const isExpiring = (sc) => sc && sc <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
  const shown = docFilter ? docs.filter(docFilter) : docs;
  const openUpload = (c) => { const cat = typeof c === "string" ? c : defaultCategoria; setMeta(cat ? { categoria: cat } : {}); setOpen(true); };
  const isObbl = (d) => obbligatori.some(([c]) => c === catOf(d));
  const altri = shown.filter((d) => !isObbl(d));
  const catsAltri = [...new Set(altri.map(catOf))];
  const lista = (obbligatori.length ? altri : shown).filter((d) => (fCat === "tutti" || catOf(d) === fCat) && (!q.trim() || `${d.titolo || ""} ${d.original_filename || ""} ${d.categoria || ""}`.toLowerCase().includes(q.trim().toLowerCase())));
  const inScad = shown.filter((d) => isExpiring(d.data_scadenza));

  return (
    <div>
      {obbligatori.length > 0 && (
        <div className="mb-4" data-testid="doc-obbligatori">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-800 mb-2">{obbligatori.length > 1 ? "Documenti obbligatori" : "Documento obbligatorio"}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {obbligatori.map(([c, , hint]) => {
              const items = shown.filter((d) => catOf(d) === c); const ok = items.length > 0;
              if (!loaded) return <div key={c} className="rounded-xl border-2 bg-white p-3 text-sm text-muted-foreground">Caricamento…</div>;
              return (
                <div key={c} className={`rounded-xl border-2 bg-white p-3 ${ok ? "border-emerald-300" : "border-red-300"}`} data-testid={`doc-obbl-${slug(c)}`}>
                  <div className="flex items-center gap-2 mb-1"><b className="text-sm">{c}</b><span className={`ml-auto text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid={`doc-obbl-stato-${slug(c)}`}>{ok ? "Caricato" : "Mancante"}</span></div>
                  {hint && <p className="text-[11px] text-muted-foreground mb-2">{hint}</p>}
                  <div className="space-y-1.5">{items.map((d) => (
                    <div key={d.id} className="flex items-center gap-2 rounded-lg border p-2 text-xs" data-testid={`doc-${d.id}`}>
                      <FileText size={14} className="text-primary shrink-0" /><span className="flex-1 min-w-0 truncate font-semibold">{d.titolo || d.original_filename}</span><span className="text-muted-foreground">{fmtDate(d.created_at)}</span>
                      {fa(d, 14)}
                      <button onClick={() => remove(d.id)} className="p-1 rounded hover:bg-secondary text-destructive" data-testid={`deldoc-${d.id}`}><Trash2 size={14} /></button>
                    </div>
                  ))}</div>
                  <Button size="sm" variant={ok ? "outline" : "default"} className="w-full h-8 text-xs font-semibold mt-2" onClick={() => openUpload(c)} data-testid={`upload-obbl-${slug(c)}`}><Upload size={13} className="mr-1" /> Carica</Button>
                </div>
              );
            })}
          </div>
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-emerald-800 mt-4">Altri documenti</div>
        </div>
      )}
      {smart && (
        <div className="space-y-3 mb-3">
          <div className="flex gap-2 flex-wrap"><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca un documento…" className="flex-1 min-w-[200px] h-10 bg-white" data-testid={`doc-cerca${testidSuffix ? "-" + testidSuffix : ""}`} /></div>
          <div className="flex gap-1.5 flex-wrap">{["tutti", ...catsAltri].map((c) => <button key={c} type="button" onClick={() => setFCat(c)} data-testid={`doc-filtro-${slug(c)}`} className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${fCat === c ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-slate-600 hover:bg-emerald-50"}`}>{c === "tutti" ? "Tutti" : c} {c === "tutti" ? altri.length : altri.filter((d) => catOf(d) === c).length}</button>)}</div>
          {inScad.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="doc-scadenza-avviso"><b>Da rinnovare:</b> {inScad.map((d) => `${d.titolo || d.original_filename} (scade il ${fmtDate(d.data_scadenza)})`).join(" · ")}</div>}
        </div>
      )}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-muted-foreground">{lista.length} documenti</span>
        <Button size="sm" onClick={openUpload} data-testid={`upload-${ownerType}${testidSuffix ? "-" + testidSuffix : ""}`}><Upload size={15} className="mr-1" /> Carica documento</Button>
      </div>
      <div className={smart ? "grid md:grid-cols-2 gap-2" : "space-y-2"}>
        {lista.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nessun documento caricato.</p>}
        {lista.map((d) => (
          <div key={d.id} className="flex items-center gap-3 p-3 rounded-lg border bg-white" data-testid={`doc-${d.id}`}>
            <FileText size={18} className="text-primary shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate">{d.titolo}</div>
              <div className="text-xs text-muted-foreground">{d.categoria || "—"} · caricato {fmtDate(d.created_at)}{d.data_scadenza && ` · scade ${fmtDate(d.data_scadenza)}`}</div>
            </div>
            {isExpiring(d.data_scadenza) && <span className="inline-flex items-center gap-1 text-xs font-bold text-red-600"><AlertTriangle size={13} /> In scadenza</span>}
            {fa(d, 15)}
            <Button size="sm" variant="ghost" onClick={() => remove(d.id)} data-testid={`deldoc-${d.id}`}><Trash2 size={15} className="text-destructive" /></Button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Carica documento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold mb-1 block">File</Label><Input type="file" ref={fileRef} data-testid="doc-file" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Titolo</Label><Input value={meta.titolo || ""} onChange={(e) => setMeta((p) => ({ ...p, titolo: e.target.value }))} data-testid="doc-titolo" /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Categoria</Label>
              <Select value={meta.categoria || ""} onValueChange={(v) => setMeta((p) => ({ ...p, categoria: v }))}>
                <SelectTrigger data-testid="doc-categoria"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold mb-1 block">Data documento</Label><Input type="date" value={meta.data_documento || ""} onChange={(e) => setMeta((p) => ({ ...p, data_documento: e.target.value }))} /></div>
              <div><Label className="text-xs font-semibold mb-1 block">Data scadenza</Label><Input type="date" value={meta.data_scadenza || ""} onChange={(e) => setMeta((p) => ({ ...p, data_scadenza: e.target.value }))} data-testid="doc-scadenza" /></div>
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Note</Label><Input value={meta.note || ""} onChange={(e) => setMeta((p) => ({ ...p, note: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={upload} disabled={uploading} data-testid="doc-save">{uploading ? "Caricamento..." : "Carica"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
